import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { CHUNKING_STRATEGIES } from '@/modules/gpt-chats/constants';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { IFolder } from '@/modules/gpt-chats/types/kb.service.type';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateFolder } from '@/modules/gpt-chats/hooks/use-kb';
import { useToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import ModelSelection from '../../../model-selection-internal/model-selection';

export const getConfigureFolderSchema = (t: TFunction) =>
  z.object({
    name: z.string().trim().min(1, t('NAME_REQUIRED')).max(50, t('NAME_MAX_LENGTH')),
    description: z
      .string()
      .trim()
      .min(1, t('DESCRIPTION_REQUIRED'))
      .max(200, t('DESCRIPTION_MAX_LENGTH')),
    llm_config: z.object({
      model_name: z.string().trim(),
      is_blocks_provider: z.boolean(),
      provider: z.string().trim(),
      model_id: z.string().trim().optional(),
    }),
    chunking_strategy: z.string().trim().min(1, t('CHUNKING_STRATEGY_REQUIRED')),
  });

export const FolderConfigureModal = ({
  open,
  onOpenChange,
  folder,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  folder: IFolder;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(getConfigureFolderSchema(t)),
    values: {
      name: folder?.Name || '',
      description: folder?.Description || '',
      llm_config: {
        model_name: folder?.EmbeddingModelConfiguration?.Model || '',
        is_blocks_provider: folder?.EmbeddingModelConfiguration?.IsProviderBlocks || false,
        provider: folder?.EmbeddingModelConfiguration?.Provider || '',
        model_id: folder?.EmbeddingModelConfiguration?.ModelId || undefined,
      },
      chunking_strategy: folder?.EmbeddingModelConfiguration?.ChunkingStrategy || 'semantic',
    },
    mode: 'onChange',
  });

  const updateFolder = useUpdateFolder();

  const submitHandler = () => {
    updateFolder.mutate(
      {
        folder_id: folder?._id || '',
        name: form.getValues('name'),
        description: form.getValues('description'),
        embedding_model_config: {
          Model: form.getValues('llm_config').model_name,
          IsProviderBlocks: form.getValues('llm_config').is_blocks_provider,
          Provider: form.getValues('llm_config').provider,
          ModelId: form.getValues('llm_config').model_id as string,
          ChunkingStrategy: form.getValues('chunking_strategy'),
        },
      },
      {
        onSuccess: () => {
          toast.success(t('FOLDER_UPDATED_SUCCESSFULLY'));
          onOpenChange?.(false);
        },
        onError: (error) => {
          toast.error({ errors: ErrorTransformer(error) });
          onOpenChange?.(false);
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange?.(v);
        if (!v) form.reset();
      }}
    >
      <DialogContent
        className="w-[calc(100%-2rem)] p-6 sm:mx-0 sm:max-w-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('CONFIGURE_FOLDER')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form>
            <div className="mt-4 flex flex-col gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('NAME_LABEL')} <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={t('ENTER_NAME')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('DESCRIPTION_LABEL')}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea placeholder={t('ENTER_DESCRIPTION')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ModelSelection
                key={form.getValues('llm_config').provider}
                form={form}
                modelType="embedding"
                isRequired={false}
                disabled={true}
              />

              <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-blue-600 dark:bg-blue-950/60 dark:text-blue-200">
                {t('EMBEDDING_MODEL_CHANGE_WARNING')}
              </div>

              <FormField
                control={form.control}
                name="chunking_strategy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('CHUNKING_STRATEGY')}</FormLabel>
                    <FormControl>
                      <Select onValueChange={(value) => field.onChange(value)} value={field.value}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('SELECT_STRATEGY')} />
                        </SelectTrigger>
                        <SelectContent>
                          {CHUNKING_STRATEGIES.map((strategy) => (
                            <SelectItem key={strategy.value} value={strategy.value}>
                              {strategy.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>

        <DialogFooter className="mt-6">
          <DialogClose asChild>
            <Button variant="outline">{t('CANCEL')}</Button>
          </DialogClose>
          <Button
            disabled={!form.formState.isValid || updateFolder.isPending}
            onClick={submitHandler}
          >
            {t('SAVE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
